import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const PendingApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [approvalStatus, setApprovalStatus] = useState('pending');
  const [isRestricted, setIsRestricted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if redirected here due to restriction
  const restrictedFromState = location?.state?.restricted === true;

  useEffect(() => {
    checkApprovalStatus();
  }, [user]);

  const checkApprovalStatus = async () => {
    try {
      if (!user?.id) {
        navigate('/student-registration');
        return;
      }

      const { data, error } = await supabase?.from('student_profiles')?.select('approval_status, is_restricted')?.eq('user_id', user?.id)?.maybeSingle();

      if (error) throw error;

      // If no student profile exists, redirect to registration
      if (!data) {
        navigate('/student-registration');
        return;
      }

      const status = data?.approval_status || 'pending';
      const restricted = data?.is_restricted === true;
      setApprovalStatus(status);
      setIsRestricted(restricted);

      // If restricted, stay on this page and show restricted message
      if (restricted) {
        setLoading(false);
        return;
      }

      // If approved and not restricted, redirect to dashboard
      if (status === 'approved') {
        navigate('/student-dashboard');
      }
    } catch (error) {
      console.error('Error checking approval status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <Icon name="Loader" className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Restricted access screen
  if (isRestricted || restrictedFromState) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="ShieldOff" className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Access Restricted
          </h1>
          <p className="text-gray-600 mb-6">
            Your access has been restricted. Please contact the admin for more information or to request reinstatement.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-red-700">
              <Icon name="AlertCircle" className="w-5 h-5" />
              <span className="font-medium text-sm">Status: Access Restricted</span>
            </div>
          </div>
          <div className="space-y-3">
            <Button
              onClick={() => window.location.href = 'mailto:support@studyexpo.in'}
              className="w-full"
              variant="primary"
            >
              <Icon name="Mail" className="w-5 h-5 mr-2" />
              Contact Admin
            </Button>
            <Button
              onClick={handleSignOut}
              className="w-full"
              variant="outline"
            >
              <Icon name="LogOut" className="w-5 h-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (approvalStatus === 'rejected') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="XCircle" className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Registration Not Approved
          </h1>
          <p className="text-gray-600 mb-6">
            Unfortunately, your registration has not been approved. Please contact support for more information.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => window.location.href = 'mailto:support@studyexpo.in'}
              className="w-full"
              variant="primary"
            >
              <Icon name="Mail" className="w-5 h-5 mr-2" />
              Contact Support
            </Button>
            <Button
              onClick={handleSignOut}
              className="w-full"
              variant="outline"
            >
              <Icon name="LogOut" className="w-5 h-5 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* Animated Icon */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-75"></div>
          <div className="relative w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
            <Icon name="Clock" className="w-12 h-12 text-blue-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          Approval Pending
        </h1>

        {/* Description */}
        <p className="text-gray-600 mb-6">
          Thank you for registering! Your account is currently under review by our admin team. 
          You will receive access to the Student Dashboard and Exhibition Hall once approved.
        </p>

        {/* Status Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-2 text-blue-700">
            <Icon name="Info" className="w-5 h-5" />
            <span className="font-medium">Status: Pending Review</span>
          </div>
        </div>

        {/* Information List */}
        <div className="text-left space-y-3 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              Your registration details have been submitted successfully
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Clock" className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              Approval typically takes 24-48 hours
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Bell" className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              You will be notified via email once your account is approved
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={checkApprovalStatus}
            className="w-full"
            variant="primary"
          >
            <Icon name="RefreshCw" className="w-5 h-5 mr-2" />
            Check Status
          </Button>
          <Button
            onClick={handleSignOut}
            className="w-full"
            variant="outline"
          >
            <Icon name="LogOut" className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Support Link */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Need help?{' '}
            <a
              href="mailto:support@studyexpo.in"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;